import InfinityIcon from '@mui/icons-material/AllInclusive'
import { Box, Button } from '@mui/material'
import InputAdornment from '@mui/material/InputAdornment'
import { DesktopDateTimePicker } from '@mui/x-date-pickers'
import { pickersLayoutClasses } from '@mui/x-date-pickers/PickersLayout'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { InputHeader } from '../Headers/styles'
import RequiredAsterisk from '../RequiredAsterisk/RequiredAsterisk'

type FormDatePickerInputProps = {
  name: string
  label?: string
  minDate?: string | Date
  maxDate?: string | Date
  disablePast?: boolean
  disabled?: boolean
  format?: string
  required?: boolean
  onChange?: (date: dayjs.Dayjs | null) => void
  allowInfinity?: boolean
  infinityLabel?: string
}

const INFINITY_VALUE = 'INFINITY'

const FormDatePickerInput: React.FC<FormDatePickerInputProps> = ({
  name,
  label,
  minDate,
  maxDate,
  disablePast = false,
  disabled = false,
  format = 'YYYY-MM-DD',
  required = false,
  onChange,
  allowInfinity = false,
  infinityLabel = 'Infinity',
}) => {
  const { control, setValue, watch } = useFormContext()
  const currentValue = watch(name)
  const [isInfinity, setIsInfinity] = useState(currentValue === INFINITY_VALUE)

  useEffect(() => {
    setIsInfinity(currentValue === INFINITY_VALUE)
  }, [currentValue])

  const handleSetInfinity = () => {
    setIsInfinity(true)
    setValue(name, INFINITY_VALUE)
    if (onChange) {
      onChange(null)
    }
  }

  return (
    <Box mb={2} width='100%'>
      <InputHeader>
        {label}
        {required ? <RequiredAsterisk /> : null}
      </InputHeader>

      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <DesktopDateTimePicker
            disablePast={disablePast}
            disabled={disabled}
            ampm={false}
            format={format}
            value={field.value && field.value !== INFINITY_VALUE ? dayjs(field.value) : null}
            minDate={dayjs(minDate)}
            maxDate={dayjs(maxDate ?? new Date('2100-01-01'))}
            views={
              format === 'YYYY'
                ? ['year']
                :
                format.includes('HH') || format.includes('mm')
                  ? ['year', 'month', 'day', 'hours', 'minutes']
                  : ['year', 'month', 'day']
            }
            onChange={(date) => {
              if (isInfinity) {
                setIsInfinity(false)
              }

              if (date && date.isValid()) {
                field.onChange(date.toDate())
                if (onChange) {
                  onChange(date)
                }
              } else {
                field.onChange(null)
                if (onChange) {
                  onChange(null)
                }
              }
            }}
            slotProps={{
              textField: {
                id: name,
                name: name,
                error: !!error,
                fullWidth: true,
                InputLabelProps: { shrink: true },
                helperText: error ? error.message : '',
                ...(allowInfinity ? { placeholder: '∞' } : {}),
                InputProps: {
                  sx: {
                    width: '100%',
                    color: isInfinity ? 'primary.main' : 'text.primary',
                    backgroundColor: '#FFFFFF',
                    position: 'relative',
                    '& input': {
                      paddingTop: '10px',
                      paddingBottom: '10px',
                    },
                    // MUI X v6 renders date sections as <span> (not <input>) so ::placeholder
                    // does not apply. Fade section content only when no date is selected.
                    ...(!field.value && {
                      '& .MuiPickersInput-sectionContent': { opacity: 0.38 },
                    }),
                  },
                  startAdornment: isInfinity && (
                    <InputAdornment
                      position='start'
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        pointerEvents: 'none',
                        margin: 0,
                      }}
                    >
                      <InfinityIcon color='primary' />
                    </InputAdornment>
                  ),
                },
              },
              layout: {
                sx: {
                  display: 'flex',
                  flexDirection: 'column',
                  [`& .${pickersLayoutClasses.contentWrapper}`]: {
                    flex: 1,
                  },
                  [`& .${pickersLayoutClasses.toolbar}`]: {
                    order: 2,
                  },
                  [`& .${pickersLayoutClasses.actionBar}`]: {
                    order: 3,
                  },
                },
              },
            }}
            slots={{
              actionBar: allowInfinity
                ? (actionBarProps) => {
                  const { className, ...otherProps } = actionBarProps
                  return (
                    <Box
                      className={className}
                      {...otherProps}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 12px',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Button
                        onClick={() => {
                          setValue(name, null)
                          setIsInfinity(false)
                          if (onChange) onChange(null)
                        }}
                        color='primary'
                      >
                        Clear
                      </Button>
                    </Box>
                  )
                }
                : undefined,
            }}
          />
        )}
      />
    </Box>
  )
}

export default FormDatePickerInput
